import { IMAGE_KINDS } from "@ondo/shared";
import type { ImageKind } from "@ondo/shared";
import { useId, useRef } from "react";
import { Link } from "react-router";

import { IMAGE_KIND_LABELS } from "../lib/constants.ts";
import { describeBytes } from "../lib/files.ts";
import { useModalDialog } from "../lib/useModalDialog.ts";
import { ACCEPTED_IMAGE_TYPES } from "../register/register.constants.ts";
import { STUDIO_REGISTER_COPY } from "./estate.constants.ts";
import { STUDIO_REGISTER_STEPS, STUDIO_STATUSES } from "./registerStudio.model.ts";
import type { StudioRegisterDraft } from "./registerStudio.model.ts";
import type { StudioRegisterView } from "./useRegisterStudioController.ts";

import "../lib/dialog.css";

/**
 * Registering a work in the studio: the four-step dialog the design draws.
 *
 * A native `<dialog>` opened with `showModal()`, for the list of things
 * `AddHistoryDialog` gives: modal semantics, Escape, a focus trap, the rest
 * of the document inert, focus returned to the button, the top layer. And
 * mounted even when shut, because it is `close()` that returns that focus.
 *
 * Closing puts the draft down; Cancel discards it — the controller's rule.
 */
export function RegisterStudioDialog({
  view,
  workBase,
  onEdit,
  onNext,
  onBack,
  onAddImages,
  onRemoveImage,
  onSetImageKind,
  onSubmit,
  onCancel,
  onClose,
  onRegisterAnother,
}: {
  view: StudioRegisterView;
  workBase: string;
  onEdit: (patch: Partial<StudioRegisterDraft>) => void;
  onNext: () => void;
  onBack: () => void;
  onAddImages: (files: File[]) => void;
  onRemoveImage: (id: string) => void;
  onSetImageKind: (id: string, kind: ImageKind) => void;
  onSubmit: () => void;
  onCancel: () => void;
  onClose: () => void;
  onRegisterAnother: () => void;
}) {
  const dialog = useModalDialog(view.open);
  const titleId = useId();
  const base = useId();

  const { stage, draft } = view;

  return (
    <dialog
      ref={dialog}
      className="studio-register"
      aria-labelledby={titleId}
      // Escape and the backdrop close natively; this tells React it happened.
      onClose={onClose}
    >
      <div className="studio-register__head ondo-dialog__head">
        <div>
          <h2 id={titleId} className="studio-register__title">
            {STUDIO_REGISTER_COPY.title}
          </h2>
          <p className="studio-register__subtitle">{STUDIO_REGISTER_COPY.subtitle}</p>
        </div>
        <button
          type="button"
          className="studio-register__close ondo-dialog__close"
          aria-label={STUDIO_REGISTER_COPY.close}
          onClick={onClose}
        >
          ✕
        </button>
      </div>

      {view.open && (
        <div className="studio-register__body">
          {stage.kind !== "registered" && (
            <ol className="studio-register__steps" aria-label="Steps">
              {STUDIO_REGISTER_STEPS.map((step, index) => (
                <li
                  key={step}
                  className={
                    index < view.step
                      ? "studio-register__step studio-register__step--done"
                      : index === view.step
                        ? "studio-register__step studio-register__step--current"
                        : "studio-register__step"
                  }
                  aria-current={index === view.step ? "step" : undefined}
                >
                  {STUDIO_REGISTER_COPY.steps[step]}
                </li>
              ))}
            </ol>
          )}

          {stage.kind === "details" && (
            <DetailsPanel base={base} draft={draft} onEdit={onEdit} />
          )}

          {stage.kind === "images" && (
            <ImagesPanel
              images={view.images}
              refusals={view.refusals}
              onAdd={onAddImages}
              onRemove={onRemoveImage}
              onSetKind={onSetImageKind}
            />
          )}

          {stage.kind === "provenance" && (
            <div className="studio-register__panel">
              <p className="studio-register__legend">{STUDIO_REGISTER_COPY.originLegend}</p>
              <p className="studio-register__origin">{STUDIO_REGISTER_COPY.origin}</p>
              <label className="estate__input">
                <span>{STUDIO_REGISTER_COPY.notes}</span>
                <textarea
                  rows={4}
                  placeholder={STUDIO_REGISTER_COPY.notesPlaceholder}
                  value={draft.notes}
                  onChange={(event) => onEdit({ notes: event.target.value })}
                />
              </label>
              <p className="estate__confirmation">{STUDIO_REGISTER_COPY.provenanceNote}</p>
            </div>
          )}

          {stage.kind === "confirm" && (
            <div className="studio-register__panel">
              <div className="studio-register__summary">
                <div className="studio-register__summary-work">
                  <strong>{draft.title.trim()}</strong>
                  <span className="estate__badge estate__badge--conceived">
                    {STUDIO_REGISTER_COPY.statuses[draft.status]}
                  </span>
                </div>
                <dl className="estate__fields">
                  {view.rows.map((row) => (
                    <div className="estate__field" key={row.label}>
                      <dt>{row.label}</dt>
                      <dd>{row.value}</dd>
                    </div>
                  ))}
                </dl>
                <p className="studio-register__code studio-register__code--pending">
                  {STUDIO_REGISTER_COPY.codePending}
                </p>
              </div>
              <p className="estate__hint">{STUDIO_REGISTER_COPY.confirmNote}</p>
            </div>
          )}

          {stage.kind === "registered" && (
            <div className="studio-register__panel" role="status">
              <p className="estate__empty-lead">
                {STUDIO_REGISTER_COPY.registeredLead(stage.title)}
              </p>
              <p className="studio-register__code">
                <span className="estate__badge estate__badge--green">
                  {STUDIO_REGISTER_COPY.neverSoldBadge}
                </span>
                <code>{stage.ondoCode}</code>
              </p>
              <p className="estate__hint">{STUDIO_REGISTER_COPY.registeredDetail}</p>
              {view.notice && (
                <p className="estate__refusal" role="alert">
                  {view.notice}
                </p>
              )}
            </div>
          )}

          {view.error && (
            <p className="estate__refusal" role="alert">
              {view.error}
            </p>
          )}

          <div className="studio-register__actions">
            {stage.kind === "details" && (
              <>
                <button type="button" className="estate__ghost" onClick={onCancel}>
                  {STUDIO_REGISTER_COPY.cancel}
                </button>
                <button
                  type="button"
                  className="estate__primary estate__primary--register"
                  disabled={draft.title.trim() === ""}
                  onClick={onNext}
                >
                  {STUDIO_REGISTER_COPY.next}
                </button>
              </>
            )}
            {(stage.kind === "images" || stage.kind === "provenance") && (
              <>
                <button type="button" className="estate__ghost" onClick={onBack}>
                  {STUDIO_REGISTER_COPY.back}
                </button>
                <button
                  type="button"
                  className="estate__primary estate__primary--register"
                  onClick={onNext}
                >
                  {STUDIO_REGISTER_COPY.next}
                </button>
              </>
            )}
            {stage.kind === "confirm" && (
              <>
                <button type="button" className="estate__ghost" onClick={onBack}>
                  {STUDIO_REGISTER_COPY.back}
                </button>
                <button
                  type="button"
                  className="estate__primary estate__primary--register"
                  disabled={view.busy}
                  onClick={onSubmit}
                >
                  {view.busy ? STUDIO_REGISTER_COPY.submitting : STUDIO_REGISTER_COPY.submit}
                </button>
              </>
            )}
            {stage.kind === "registered" && (
              <>
                <button type="button" className="estate__ghost" onClick={onRegisterAnother}>
                  {STUDIO_REGISTER_COPY.registerAnother}
                </button>
                <Link className="estate__ghost" to={`${workBase}/${stage.workId}`}>
                  {STUDIO_REGISTER_COPY.openRecord}
                </Link>
                <button type="button" className="estate__primary" onClick={onClose}>
                  {STUDIO_REGISTER_COPY.done}
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </dialog>
  );
}

/** Step 1. The fields the design draws, plus the currency the schema needs. */
function DetailsPanel({
  base,
  draft,
  onEdit,
}: {
  base: string;
  draft: StudioRegisterDraft;
  onEdit: (patch: Partial<StudioRegisterDraft>) => void;
}) {
  const field = (
    label: string,
    key: Exclude<keyof StudioRegisterDraft, "status">,
    placeholder = "",
  ) => (
    <label className="estate__input">
      <span>{label}</span>
      <input
        onChange={(event) => onEdit({ [key]: event.target.value })}
        placeholder={placeholder}
        value={draft[key]}
      />
    </label>
  );

  return (
    <div className="studio-register__panel">
      {field(STUDIO_REGISTER_COPY.workTitle, "title", STUDIO_REGISTER_COPY.titlePlaceholder)}
      <div className="estate__form-grid">
        {field(STUDIO_REGISTER_COPY.year, "year", STUDIO_REGISTER_COPY.yearPlaceholder)}
        {field(STUDIO_REGISTER_COPY.medium, "medium", STUDIO_REGISTER_COPY.mediumPlaceholder)}
        {field(
          STUDIO_REGISTER_COPY.dimensions,
          "dimensions",
          STUDIO_REGISTER_COPY.dimensionsPlaceholder,
        )}
        {field(STUDIO_REGISTER_COPY.edition, "editionSize", "1")}
        <label className="estate__input">
          <span>{STUDIO_REGISTER_COPY.status}</span>
          <select
            id={`${base}-status`}
            value={draft.status}
            onChange={(event) =>
              onEdit({ status: event.target.value as StudioRegisterDraft["status"] })
            }
          >
            {STUDIO_STATUSES.map((status) => (
              <option key={status} value={status}>
                {STUDIO_REGISTER_COPY.statuses[status]}
              </option>
            ))}
          </select>
        </label>
        {/* Only In production needs a number; Conceived and Produced imply theirs. */}
        {draft.status === "in_production" &&
          field(STUDIO_REGISTER_COPY.producedSoFar, "producedSoFar", "1")}
        {field(
          STUDIO_REGISTER_COPY.location,
          "location",
          STUDIO_REGISTER_COPY.locationPlaceholder,
        )}
        {field(STUDIO_REGISTER_COPY.cost, "cost", "e.g. 42000")}
        {field(STUDIO_REGISTER_COPY.insured, "insured", "e.g. 180000")}
        {field(STUDIO_REGISTER_COPY.retail, "retail", "e.g. 240000")}
        {field(STUDIO_REGISTER_COPY.series, "series", "e.g. 1680000")}
        {field(STUDIO_REGISTER_COPY.currency, "currency", "GBP")}
      </div>
    </div>
  );
}

/** Step 2. A picker and the thumbnails; the same intake the collector's wizard screens with. */
function ImagesPanel({
  images,
  refusals,
  onAdd,
  onRemove,
  onSetKind,
}: {
  images: { id: string; file: File; kind: ImageKind; previewUrl: string }[];
  refusals: string[];
  onAdd: (files: File[]) => void;
  onRemove: (id: string) => void;
  onSetKind: (id: string, kind: ImageKind) => void;
}) {
  const picker = useRef<HTMLInputElement>(null);

  return (
    <div className="studio-register__panel">
      <div className="studio-register__drop">
        <span aria-hidden="true">⊕</span>
        <strong>{STUDIO_REGISTER_COPY.imagesLead}</strong>
        <span className="estate__hint">{STUDIO_REGISTER_COPY.imagesDetail}</span>
        <input
          ref={picker}
          type="file"
          multiple
          accept={ACCEPTED_IMAGE_TYPES.join(",")}
          onChange={(event) => {
            onAdd([...(event.target.files ?? [])]);
            // Cleared so re-picking the same file after removing it still fires.
            event.target.value = "";
          }}
        />
        <button type="button" className="estate__ghost" onClick={() => picker.current?.click()}>
          {STUDIO_REGISTER_COPY.addImage}
        </button>
      </div>

      {refusals.length > 0 && (
        <ul className="estate__refusal" role="alert">
          {refusals.map((refusal) => (
            <li key={refusal}>{refusal}</li>
          ))}
        </ul>
      )}

      {images.length > 0 && (
        <ul className="studio-register__thumbs">
          {images.map((image) => (
            <li key={image.id} className="studio-register__thumb">
              <img src={image.previewUrl} alt={image.file.name} />
              <select
                aria-label={STUDIO_REGISTER_COPY.imageKind(image.file.name)}
                value={image.kind}
                onChange={(event) => onSetKind(image.id, event.target.value as ImageKind)}
              >
                {IMAGE_KINDS.map((kind) => (
                  <option key={kind} value={kind}>
                    {IMAGE_KIND_LABELS[kind]}
                  </option>
                ))}
              </select>
              <span className="estate__hint">{describeBytes(image.file.size)}</span>
              <button type="button" className="estate__ghost" onClick={() => onRemove(image.id)}>
                {STUDIO_REGISTER_COPY.removeImage}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
